import React, { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import CustomDropdown from "@cayuse/shared-react.custom-dropdown";
import { useAuth } from "@cayuse/shared-react.utils.auth";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import SPLink from "components/SPLink";
import {
	ATTACHMENT_TYPES_BASE_ROUTE,
	AWARDS_BASE_ROUTE,
	BUDGET_SETTINGS_BASE_ROUTE,
	CERTIFICATION_TEXT_BASE_ROUTE,
	FORMS_BASE_ROUTE,
	MODIFICATIONS_BASE_ROUTE,
	PRIVILEGES,
	PROJECTS_BASE_ROUTE,
	PROPOSALS_BASE_ROUTE,
	REPORTING_PROPOSALS_ROUTE,
	REPORTING_AWARDS_ROUTE,
	RESOLUTIONS_BASE_ROUTE,
	MANAGE_RT_ROLES_ROUTE,
	SUBAWARDS_BASE_ROUTE,
	REPORTING_SUBAWARDS_ROUTE,
} from "globals/constants";
import {
	useDictionary,
	useHasPrivileges,
	useIsAuthFinished,
} from "hooks";
import { tenantSelectors } from "store/selectors";

import styles from "./styles.css";

const NavBar = ({ isFormManager }) => {
	const { loading: isLoading } = useAuth();
	const hasAuthenticationFinished = useIsAuthFinished();
	const dictionary = useDictionary("navBar");
	const history = useHistory();
	const location = useLocation();
	const { budgetBuilderEnabled } = useSelector(tenantSelectors.tenant);

	const {
		[PRIVILEGES.PROJECTS_READ]: canViewProjects,
		[PRIVILEGES.PROPOSALS_READ]: canViewProposals,
		[PRIVILEGES.AWARDS_READ]: canViewAwards,
		[PRIVILEGES.SUBAWARDS_READ]: canViewSubawards,
		[PRIVILEGES.FORM_BUILDER_EDIT]: canEditForms,
		[PRIVILEGES.RESOLUTIONS_EDIT]: canEditResolutions,
		[PRIVILEGES.MODIFICATIONS_EDIT]: canEditModifications,
		[PRIVILEGES.ATTACHMENT_TYPES_EDIT]: canEditAttachmentTypes,
		[PRIVILEGES.CERTIFICATION_TEXT_EDIT]: canEditCertificationText,
		[PRIVILEGES.RESEARCH_TEAM_ROLES_EDIT]: canEditResearchTeamRoles,
	} = useHasPrivileges([
		PRIVILEGES.PROJECTS_READ,
		PRIVILEGES.PROPOSALS_READ,
		PRIVILEGES.AWARDS_READ,
		PRIVILEGES.SUBAWARDS_READ,
		PRIVILEGES.FORM_BUILDER_EDIT,
		PRIVILEGES.RESOLUTIONS_EDIT,
		PRIVILEGES.MODIFICATIONS_EDIT,
		PRIVILEGES.ATTACHMENT_TYPES_EDIT,
		PRIVILEGES.CERTIFICATION_TEXT_EDIT,
		PRIVILEGES.RESEARCH_TEAM_ROLES_EDIT,
	]);

	const moreMenuItems = useMemo(() => {
		const allMenuItems = [
			{
				label: dictionary.forms || "Forms",
				destination: FORMS_BASE_ROUTE,
				hasAccess: canEditForms || isFormManager
			},
			{
				label: dictionary.manageResolutions || "Manage Resolutions",
				destination: RESOLUTIONS_BASE_ROUTE,
				hasAccess: canEditResolutions
			},
			{
				label: dictionary.manageModifications || "Manage Modifications",
				destination: MODIFICATIONS_BASE_ROUTE,
				hasAccess: canEditModifications
			},
			{
				label: dictionary.attachmentTypes || "Attachment Types",
				destination: ATTACHMENT_TYPES_BASE_ROUTE,
				hasAccess: canEditAttachmentTypes
			},
			{
				label: dictionary.budgetSettings || "Budget Settings",
				destination: BUDGET_SETTINGS_BASE_ROUTE,
				hasAccess: budgetBuilderEnabled && canEditAttachmentTypes
			},
			{
				label: dictionary.manageCertificationText || "Manage Certification Text",
				destination: CERTIFICATION_TEXT_BASE_ROUTE,
				hasAccess: canEditCertificationText
			},
			{
				label: dictionary.manageRTRoles || "Manage Research Team Roles",
				destination: MANAGE_RT_ROLES_ROUTE,
				hasAccess: canEditResearchTeamRoles,
			},
		];

		return allMenuItems.filter(({ hasAccess, label, destination }) => {
			const hasRequiredElements = isNonEmptyString(label) && isNonEmptyString(destination);

			return hasAccess && hasRequiredElements;
		});
	}, [
		budgetBuilderEnabled,
		canEditAttachmentTypes,
		canEditCertificationText,
		canEditForms,
		canEditModifications,
		canEditResolutions,
		dictionary,
		isFormManager
	]);

	const hasMoreMenu = isNonEmptyArray(moreMenuItems);

	const reportingMenuItems = useMemo(() => {
		const menuItems = [
			{
				label: dictionary.proposalReport || "Generate Proposal Report",
				destination: REPORTING_PROPOSALS_ROUTE,
				hasAccess: true
			},
			{
				label: dictionary.awardReport || "Generate Award Report",
				destination: REPORTING_AWARDS_ROUTE,
				hasAccess: true
			},
			{
				label: dictionary.subawardReport || "Generate Subaward Report",
				destination: REPORTING_SUBAWARDS_ROUTE,
				hasAccess: true
			}
		];

		return menuItems.filter(({ hasAccess, label, destination }) => {
			const hasRequiredElements = isNonEmptyString(label) && isNonEmptyString(destination);

			return hasAccess && hasRequiredElements;
		});
	}, [ dictionary.awardReport, dictionary.subawardReport, dictionary.proposalReport ]);

	const isActive = useCallback(route => {
		const path = location.pathname.replace("/sp", "");

		const isNestedSubaward = path.startsWith("/awards/") && path.includes("/sub-awards/");

		if (route === SUBAWARDS_BASE_ROUTE) {
			return (path.startsWith(SUBAWARDS_BASE_ROUTE) || isNestedSubaward) ? "active" : "";
		}

		if (route === AWARDS_BASE_ROUTE) {
			return (path.startsWith(AWARDS_BASE_ROUTE) && !isNestedSubaward) ? "active" : "";
		}

		return path.startsWith(route) ? "active" : "";
	}, [ location.pathname ]);


	const createMenuOption = useCallback(({ label, destination }) => ({
		className: `${styles.menuDropdownItem} ${isActive(destination)}`,
		label,
		value: destination,
		onClick: value => history.push(`/sp${value}`)
	}),
	[ isActive, history ]);

	const moreMenuOptions = useMemo(() => moreMenuItems.map(createMenuOption),
		[ moreMenuItems, createMenuOption ]);

	const reportingMenuOptions = useMemo(() => reportingMenuItems.map(createMenuOption),
		[ reportingMenuItems, createMenuOption ]);

	const onKeyDown = event => {
		if (event.key === "Enter") {
			event.target.click();
			event.target.blur();
			event.target.parentElement.setAttribute("data-pressed", true);
		} else if (event.key === "Tab") {
			event.target.parentElement.setAttribute("data-pressed", true);
		}
	};

	if (!hasAuthenticationFinished || isLoading) {
		return (
			<nav className="NavBar" aria-labelledby="mainNavigationLabel">
				<h2 id="mainNavigationLabel" className="accessibility-text">{dictionary.navBarHeader || "Site Navigation"}</h2>
			</nav>
		);
	}

	return (
		<div id="menubar" className={styles.navbar}>
			<nav className="NavBar" aria-labelledby="mainNavigationLabel">
				<h2 id="mainNavigationLabel" className="accessibility-text">Sponsored Projects</h2>
				<ul className={styles.navbarList}>
					<li onKeyDown={onKeyDown} data-pressed={false} className={styles.navbarListItem}>
						<SPLink
							className={`menuitem ${styles.menuItem} ${isActive("/proposals")}`}
							to={PROPOSALS_BASE_ROUTE}
							aria-current={isActive("/proposals") ? "page" : "false"}
						>
							{dictionary.proposals || "Proposals"}
						</SPLink>
					</li>

					<li onKeyDown={onKeyDown} data-pressed={false} className={styles.navbarListItem}>
						<SPLink
							className={`item ${styles.menuItem} ${isActive("/projects")}`}
							to={PROJECTS_BASE_ROUTE}
							aria-current={isActive("/projects") ? "page" : "false"}
						>
							{dictionary.projects || "Projects"}
						</SPLink>
					</li>

					<li onKeyDown={onKeyDown} data-pressed={false} className={styles.navbarListItem}>
						<SPLink
							className={`item ${styles.menuItem} ${isActive("/awards")}`}
							to={AWARDS_BASE_ROUTE}
							aria-current={isActive("/awards") ? "page" : "false"}
						>
							{dictionary.awards || "Awards"}
						</SPLink>
					</li>

					<li onKeyDown={onKeyDown} data-pressed={false} className={styles.navbarListItem}>
						<SPLink
							className={`item ${styles.menuItem} ${isActive("/subawards")}`}
							to={SUBAWARDS_BASE_ROUTE}
							aria-current={isActive("/subawards") ? "page" : "false"}
						>
							{dictionary.subawards  || "Subawards"}
						</SPLink>
					</li>

					<li className={styles.navbarListItem}>
						<div role="application" aria-roledescription="menu">
							<CustomDropdown
								label={dictionary.reporting || "Reporting"}
								options={reportingMenuOptions}
							/>
						</div>
					</li>

					{hasMoreMenu && (
						<li className={styles.navbarListItem}>
							<div role="application" aria-roledescription="menu">
								<CustomDropdown
									label={dictionary.more || "More"}
									options={moreMenuOptions}
								/>
							</div>
						</li>
					)}
				</ul>
			</nav>
		</div>
	);
};

export default NavBar;
